/**
 * Panel de administración: gestión de usuarios (roles) y embarques (crear, eliminar).
 * Solo accesible para admins.
 */
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { Search, Ship, Users } from 'lucide-react';
import { apiRequest, ApiError } from '@/services/apiClient';
import { storage } from '@/services/storage';
import { useEmbarkations } from '@/features/embarkations/hooks/useEmbarkations';
import type { Embarkation } from '@/features/embarkations/services/embarkationsService';
import Header from '@/components/Header';
import ConfirmDialog from '@/components/ConfirmDialog';
import dialogStyles from '@/components/ConfirmDialog.module.css';
import styles from './AdminPage.module.css';

interface User {
    id: number;
    username: string;
    email?: string | null;
    nombre: string;
    rol: string;
    aprobado: boolean;
}

export function AdminPage() {
    const [users, setUsers] = useState<User[]>([]);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<'users' | 'embarkations'>('users');
    const [newEmbarkationName, setNewEmbarkationName] = useState("");
    const [embarkationToDelete, setEmbarkationToDelete] = useState<{ id: number; nombre: string } | null>(null);
    const [userSearch, setUserSearch] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const userDataStr = storage.getItem('userData');
        if (!userDataStr) { navigate('/', { replace: true }); return; }
        try {
            const me = JSON.parse(userDataStr);
            if (me.rol !== 'admin') { navigate('/', { replace: true }); return; }
        } catch { navigate('/', { replace: true }); }
    }, [navigate]);

    const { embarkations, fetchEmbarkations, addEmbarkation, deleteEmbarkation } =
        useEmbarkations();

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const userDataStr = storage.getItem('userData');
            if (userDataStr) {
                const me = JSON.parse(userDataStr);
                setCurrentUserId(me.id);
            }
            const data = await apiRequest<{ success: boolean; users: User[] }>('/admin/usuarios');
            setUsers(data.users);
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : 'No se pudo conectar con el servidor';
            alert(message);
            if (error instanceof ApiError && error.status === 403) navigate('/login', { replace: true });
        } finally {
            setLoading(false);
        }
    };

    const fetchEmbarkationsAndLoad = async () => {
        setLoading(true);
        await fetchEmbarkations();
        setLoading(false);
    };

    useEffect(() => {
        if (view === 'users') fetchUsers();
        else fetchEmbarkationsAndLoad();
    }, [view]);

    const visibleUsers = useMemo(
        () => users.filter(u => u.id !== currentUserId),
        [users, currentUserId],
    );

    const filteredUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        if (!q) return visibleUsers;
        return visibleUsers.filter(
            u =>
                u.username.toLowerCase().includes(q) ||
                (u.email && u.email.toLowerCase().includes(q)) ||
                (u.nombre && u.nombre.toLowerCase().includes(q)),
        );
    }, [visibleUsers, userSearch]);

    const handleAddEmbarkation = async () => {
        if (!newEmbarkationName.trim()) return;
        const row = await addEmbarkation(newEmbarkationName);
        if (row) {
            setNewEmbarkationName("");
            alert('Embarque añadido');
        } else {
            alert('No se pudo añadir');
        }
    };

    const confirmDeleteEmbarkation = async () => {
        if (!embarkationToDelete) return;
        try {
            const ok = await deleteEmbarkation(embarkationToDelete.id);
            if (!ok) alert('No se pudo eliminar');
        } catch {
            alert('No se pudo eliminar');
        } finally {
            setEmbarkationToDelete(null);
        }
    };

    const toggleActive = async (userId: number, currentlyActive: boolean) => {
        const activo = !currentlyActive;
        const label = activo ? 'reactivar' : 'desactivar';
        if (!window.confirm(`¿Seguro que quieres ${label} a este usuario?`)) return;
        try {
            await apiRequest(`/admin/usuarios/${userId}/activo`, {
                method: 'PATCH',
                body: { activo },
            });
            setUsers(prev => prev.map(u => (u.id === userId ? { ...u, aprobado: activo } : u)));
        } catch (error) {
            alert(error instanceof Error ? error.message : 'Error de conexión');
        }
    };

    const toggleRole = async (userId: number, currentRole: string) => {
        const newRole = currentRole === 'admin' ? 'usuario' : 'admin';
        try {
            await apiRequest(`/admin/usuarios/${userId}/rol`, { method: 'PATCH', body: { rol: newRole } });
            setUsers(prev => prev.map(u => (u.id === userId ? { ...u, rol: newRole } : u)));
        } catch (error) {
            alert(error instanceof Error ? error.message : 'Error de conexión');
        }
    };

    return (
        <div className={styles.wrapper}>
            <Header title="Configuración" />

            <div className={styles.container}>
                <div className={styles.contentInner}>
                    <div className={styles.controlStack}>
                        <div className={styles.segmentTrack} role="tablist" aria-label="Administración">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={view === 'users'}
                                className={`${styles.segment} ${view === 'users' ? styles.segmentActive : ''}`}
                                onClick={() => setView('users')}
                            >
                                <Users size={17} strokeWidth={2} className={styles.segmentIcon} aria-hidden />
                                Usuarios
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={view === 'embarkations'}
                                className={`${styles.segment} ${view === 'embarkations' ? styles.segmentActive : ''}`}
                                onClick={() => setView('embarkations')}
                            >
                                <Ship size={17} strokeWidth={2} className={styles.segmentIcon} aria-hidden />
                                Embarques
                            </button>
                        </div>

                        {view === 'users' && (
                            <div className={styles.searchStrip} role="search">
                                <span className={styles.searchIcon} aria-hidden>
                                    <Search size={18} strokeWidth={2} />
                                </span>
                                <input
                                    type="search"
                                    className={styles.searchStripInput}
                                    placeholder="Buscar por correo o nombre"
                                    value={userSearch}
                                    onChange={e => setUserSearch(e.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                        )}

                        {view === 'embarkations' && (
                            <div className={styles.addEmbarkRow}>
                                <div className={styles.searchStrip}>
                                    <input
                                        className={styles.searchStripInput}
                                        placeholder="Crear nuevo embarque"
                                        value={newEmbarkationName}
                                        onChange={e => setNewEmbarkationName(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleAddEmbarkation(); }}
                                    />
                                </div>
                                <button type="button" className={styles.addBtn} onClick={handleAddEmbarkation}>
                                    Añadir
                                </button>
                            </div>
                        )}
                    </div>

                {loading ? (
                    <div className={styles.loadingContainer}><div className="spinner" /></div>
                ) : (
                    <div className={styles.list}>
                        {view === 'users' ? (
                            visibleUsers.length === 0 ? (
                                <p className={styles.empty}>No hay datos registrados</p>
                            ) : filteredUsers.length === 0 ? (
                                <p className={styles.empty}>No hay resultados para la búsqueda</p>
                            ) : (
                                filteredUsers.map(item => (
                                    <div key={item.id} className={styles.userCard}>
                                        <div className={styles.userInfo}>
                                            <div className={styles.identityRow}>
                                                <span className={styles.username}>{item.email || item.username}</span>
                                                {!item.aprobado && (
                                                    <span className={`${styles.statusBadge} ${styles.statusPending}`}>
                                                        <span className={styles.statusText}>Desactivado</span>
                                                    </span>
                                                )}
                                                <span className={styles.roleTag}>{item.rol === 'admin' ? 'Admin' : 'Usuario'}</span>
                                            </div>
                                            {item.nombre && <span className={styles.name}>{item.nombre}</span>}
                                        </div>
                                        <div className={styles.actions}>
                                            <button
                                                type="button"
                                                className={`${styles.actionButton} ${item.aprobado ? styles.actionBtnDanger : styles.actionBtnPrimary}`}
                                                onClick={() => toggleActive(item.id, item.aprobado)}
                                            >
                                                {item.aprobado ? 'Desactivar' : 'Reactivar'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.actionButton} ${styles.actionBtnPrimary}`}
                                                onClick={() => toggleRole(item.id, item.rol)}
                                            >
                                                {item.rol === 'admin' ? 'Hacer usuario' : 'Hacer admin'}
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )
                        ) : (
                            embarkations.length === 0 ? (
                                <p className={styles.empty}>No hay datos registrados</p>
                            ) : (
                                embarkations.map((item: Embarkation) => (
                                    <div key={item.id} className={styles.userCard}>
                                        <div className={styles.userInfo}>
                                            <span className={styles.username}>{item.nombre}</span>
                                        </div>
                                        <div className={`${styles.actions} ${styles.actionsEmbark}`}>
                                            <span className={styles.embarkIdMeta}>ID {item.id}</span>
                                            <button type="button" className={`${styles.actionButton} ${styles.actionBtnDanger}`} onClick={() => setEmbarkationToDelete({ id: item.id, nombre: item.nombre })}>
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )
                        )}
                    </div>
                )}
                </div>
            </div>

            <ConfirmDialog
                open={embarkationToDelete != null}
                title="Eliminar Embarque"
                onCancel={() => setEmbarkationToDelete(null)}
                onConfirm={confirmDeleteEmbarkation}
            >
                <p>
                    ¿Estás seguro de que quieres eliminar el embarque <strong className={dialogStyles.modalStrong}>{embarkationToDelete?.nombre}</strong>?
                    Esta acción no se puede deshacer.
                </p>
            </ConfirmDialog>
        </div>
    );
}
